import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { useMixpanel } from '../../contexts';
import { MixpanelEvent } from '../../tokens';
import { format, readableDay, type DateFnInput } from '../../utils';
import { Alert, AlertVariants } from '../Alert';
import { AlertBanner } from '../AlertBanner';
import { Button, IconButton } from '../Button';
import { Box, HStack, Stack } from '../Core';
import { Divider } from '../Divider';
import { FormControlSizes } from '../Form';
import { Icon, ICON_SIZES, IconName } from '../Icons';
import { FormattedMessage } from '../Intl';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import type { RestBlotterPaginationLimit } from './types';
import type { UseWsBlotterTable } from './useWsBlotterTable';

const messages = defineMessages({
  tooManyRecords: {
    defaultMessage: 'Too many records ({recordsReceived}), only showing results through {date}',
    id: 'BlotterTable.tooManyRecords',
  },
  tooManyRecordsRest: {
    defaultMessage: 'Display limit reached. Only the first {recordsReceived} entries are shown.',
    id: 'BlotterTable.tooManyRecordsRest',
  },
  loadingAllRecords: {
    defaultMessage: 'Loading all records...',
    id: 'BlotterTable.loadingAllRecords',
  },
  loadAllRecords: {
    defaultMessage: 'Load All',
    id: 'BlotterTable.loadAllRecords',
  },
  loadMoreRecords: {
    defaultMessage: 'Load more records',
    id: 'BlotterTable.loadMoreRecords',
  },
  loadMoreRecordsDescription: {
    defaultMessage:
      'Raise the pagination limit. If done excessively this can cause performance issues with the application.',
    id: 'BlotterTable.loadMoreRecordsDescription',
  },
  dismiss: {
    defaultMessage: 'Dismiss',
    id: 'BlotterTable.dismiss',
  },
});

type WSBlotterTableMaxRecordsReachedWarningProps<
  TOutputType extends object,
  TInputType extends object = TOutputType,
> = UseWsBlotterTable<TOutputType, TInputType>['paginationLimit'] & {
  getTimestamp: (item: TOutputType) => string | undefined;
};

function useLimitReachedVisibility(limitReached: boolean | undefined) {
  const [showLimitReached, setShowLimitReached] = useState(!!limitReached);

  useEffect(() => {
    setShowLimitReached(!!limitReached);
  }, [limitReached]);

  return {
    showLimitReached,
    dismiss: () => setShowLimitReached(false),
  };
}

export function WSBlotterTableMaxRecordsReachedWarning<TRowType extends object>({
  limitReached,
  lastRecord,
  recordsReceived,
  raiseLimit,
  getTimestamp,
}: WSBlotterTableMaxRecordsReachedWarningProps<TRowType>) {
  const visibility = useLimitReachedVisibility(limitReached);

  return (
    <MaxRecordsReachedWarning
      {...visibility}
      earliestTimestamp={lastRecord ? getTimestamp(lastRecord) : undefined}
      recordsReceived={recordsReceived}
      onRaiseLimitClicked={raiseLimit}
    />
  );
}

export function MobileWSBlotterTableMaxRecordsReachedWarning<TRowType extends object>({
  limitReached,
  lastRecord,
  recordsReceived,
  raiseLimit,
  getTimestamp,
}: WSBlotterTableMaxRecordsReachedWarningProps<TRowType>) {
  return (
    <MobileMaxRecordsReachedWarning
      showLimitReached={!!limitReached}
      earliestTimestamp={lastRecord ? getTimestamp(lastRecord) : undefined}
      recordsReceived={recordsReceived}
      onRaiseLimitClicked={raiseLimit}
    />
  );
}

export function RestBlotterTableMaxRecordsReachedWarning({
  limitReached,
  recordsReceived,
  loadAll,
  isLoading,
}: RestBlotterPaginationLimit) {
  const mixpanel = useMixpanel();
  // We hold our own state here to enable dismissing of the limit reached alert.
  const [showLimitReached, setShowLimitReached] = useState(!!limitReached);

  useEffect(() => {
    // Only hide the banner if limitReached is false AND we're not loading
    // This prevents the banner from briefly disappearing when loading starts
    if (limitReached) {
      setShowLimitReached(true);
    } else if (!isLoading) {
      setShowLimitReached(false);
    }
  }, [limitReached, isLoading]);

  const handleLoadAllClicked = () => {
    mixpanel.track(MixpanelEvent.RaisePaginationLimit);
    loadAll();
  };

  const formattedRecordsReceived = format(recordsReceived);

  // Show banner during loading OR when limit is reached (and user hasn't dismissed)
  if (!showLimitReached && !isLoading) {
    return null;
  }

  return (
    <>
      <AlertBanner variant={AlertVariants.Notice}>
        {isLoading ? (
          <FormattedMessage {...messages.loadingAllRecords} />
        ) : (
          <FormattedMessage {...messages.tooManyRecordsRest} values={{ recordsReceived: formattedRecordsReceived }} />
        )}
        {!isLoading && (
          <>
            <Divider orientation="vertical" ml="spacing4" mr="-spacing4" />
            <Box my="-spacing4">
              <Button
                ghost
                onClick={handleLoadAllClicked}
                size={FormControlSizes.Small}
                endIcon={IconName.Refresh}
                data-testid="rest-blotter-warning-load-all-button"
              >
                <FormattedMessage {...messages.loadAllRecords} />
              </Button>
            </Box>
            <Divider orientation="vertical" ml="spacing4" mr="-spacing4" />
            <Box my="-spacing4">
              <Button
                ghost
                endIcon={IconName.Close}
                size={FormControlSizes.Small}
                onClick={() => setShowLimitReached(false)}
                data-testid="rest-blotter-warning-dismiss-button"
              >
                <FormattedMessage {...messages.dismiss} />
              </Button>
            </Box>
          </>
        )}
      </AlertBanner>
      <Divider mx="-spacing24" color="backgroundDivider" />
    </>
  );
}

interface MaxRecordsReachedWarningProps {
  showLimitReached: boolean;
  recordsReceived?: number;
  dismiss?: () => void;
  earliestTimestamp?: DateFnInput;
  onRaiseLimitClicked?: () => void;
}

export function MaxRecordsReachedWarning({
  showLimitReached,
  dismiss,
  recordsReceived,
  earliestTimestamp,
  onRaiseLimitClicked,
}: MaxRecordsReachedWarningProps) {
  const theme = useTheme();
  const mixpanel = useMixpanel();

  const handleRaiseLimitClicked = () => {
    mixpanel.track(MixpanelEvent.RaisePaginationLimit);
    onRaiseLimitClicked?.();
  };

  const showRaiseLimitButton = onRaiseLimitClicked != null;
  const formattedRecordsReceived = format(recordsReceived);

  return (
    <>
      {showLimitReached && (
        <Alert
          variant={AlertVariants.Warning}
          dismissable={!!dismiss}
          onDismiss={dismiss}
          mx="spacing12"
          my="spacing4"
          alignItems="center"
        >
          <HStack w="100%" justifyContent="flex-start" gap="spacing12">
            <Text size={theme.fontSizeDefault} color={theme.colors.gray['100']}>
              <FormattedMessage
                {...messages.tooManyRecords}
                values={{ recordsReceived: formattedRecordsReceived, date: readableDay(earliestTimestamp, true) }}
              />
            </Text>
            {showRaiseLimitButton && (
              <Tooltip tooltip={<FormattedMessage {...messages.loadMoreRecordsDescription} />}>
                <Button onClick={handleRaiseLimitClicked} size={FormControlSizes.Small}>
                  <FormattedMessage {...messages.loadMoreRecords} />
                </Button>
              </Tooltip>
            )}
          </HStack>
        </Alert>
      )}
    </>
  );
}

export function MobileMaxRecordsReachedWarning({
  showLimitReached,
  dismiss,
  recordsReceived,
  earliestTimestamp,
  onRaiseLimitClicked,
}: MaxRecordsReachedWarningProps) {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const mixpanel = useMixpanel();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [earliestTimestamp, recordsReceived, showLimitReached]);

  if (!showLimitReached || dismissed) {
    return null;
  }

  const handleRaiseLimitClicked = () => {
    mixpanel.track(MixpanelEvent.RaisePaginationLimit);
    onRaiseLimitClicked?.();
  };

  const handleDismiss = () => {
    setDismissed(true);
    dismiss?.();
  };

  return (
    <Alert
      variant={AlertVariants.Warning}
      dismissable={false}
      showIcon={false}
      isBordered={false}
      borderRadius={0}
      data-testid="mobile-max-records-warning"
    >
      <Stack w="100%" px="spacing8" gap="spacing8">
        <HStack w="100%" gap="spacing4" data-testid="mobile-max-records-warning-top-row">
          <Icon
            icon={IconName.ExclamationSolid}
            color={theme.colors.yellow.lighten}
            data-testid="mobile-max-records-warning-icon"
            size={ICON_SIZES.LARGE}
          />
          <Text
            flex={1}
            size={theme.fontSizeDefault}
            color={theme.colors.gray['100']}
            textAlign="center"
            data-testid="mobile-max-records-warning-message"
          >
            <FormattedMessage
              {...messages.tooManyRecords}
              values={{ recordsReceived: format(recordsReceived), date: readableDay(earliestTimestamp, true) }}
            />
          </Text>
          <IconButton
            aria-label={formatMessage(messages.dismiss)}
            data-testid="mobile-max-records-warning-dismiss-button"
            onClick={handleDismiss}
            icon={IconName.Close}
            color="gray.100"
            ghost
          />
        </HStack>
        {onRaiseLimitClicked && (
          <Button
            width="100%"
            onClick={handleRaiseLimitClicked}
            data-testid="mobile-max-records-warning-load-more-button"
          >
            <FormattedMessage {...messages.loadMoreRecords} />
          </Button>
        )}
      </Stack>
    </Alert>
  );
}
